import isMobile from 'is-mobile';
import mobileTapSkip from '../vod/features/mobile-tap-skip';
import { JWPlayerConfig } from '@src/modules/players/shared/interfaces/player-configs.interface';
import {
  playerCustomOptionSchema,
  playerSkinOptionSchema,
} from '@src/validator/player-custom-option.schema';

import JWPlayer = jwplayer.JWPlayer;
import { savePlaybackRate } from '@root/private/js/lib/storage.util';
import { printWatermark as printWatermarkJW } from '@root/private/js/jw/common/print-watermark';
import addSkipBtn from '@root/private/js/jw/common/add-skip-btn';
import { FRONTEND_MESSAGES } from '@root/private/js/lib/messages';
import { requestFullscreenWithReport } from '@root/private/js/jw/common/fullscreen-report';
import {
  captureFailure,
  setSentryTags,
} from '@root/private/js/common/sentry';
declare const _playerOptions: JWPlayerConfig;

const setSeekStep = (playerInstance: JWPlayer, seekStep: number) => {
  // default seek step은 5초
  const isHls = location.pathname.includes('hls');
  $('body').on('keydown', function (key_evt) {
    const currentPosition = playerInstance.getPosition();
    const maxPosition = playerInstance.getDuration();
    if (key_evt.key !== 'ArrowLeft' && key_evt.key !== 'ArrowRight') return;
    let newPosition = currentPosition;
    if (key_evt.key === 'ArrowLeft') {
      newPosition -= seekStep - (isHls ? 5 : 0);
      if (newPosition < 0) newPosition = 0;
    } else {
      newPosition += seekStep - (isHls ? 5 : 0);
      if (newPosition >= maxPosition) newPosition = maxPosition;
    }

    playerInstance.seek(newPosition);
  });
};

const disableResizeMobilePlayer = (mobileSizingOption: boolean) => {
  if (mobileSizingOption !== false || isMobile()) return;
  $('.player-container').addClass('no-mobile-resize');
};

const addMobileTapSkipFeature = () => {
  if (
    !isMobile() ||
    _playerOptions.customOption.noControls === true ||
    _playerOptions.customOption.noSkip === true
  )
    return;
  mobileTapSkip();
};

const setCustomIcon = () => {
  if (!_playerOptions.skin) return;
  const { playIconUrl, customIcon, playIconCustomCss } = _playerOptions.skin;
  if (playIconUrl) {
    $('style').append(`
         .jw-display-icon-container.jw-display-icon-display.jw-reset > div .jw-svg-icon-play {
          background: url("${playIconUrl}");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: left center;
          padding: 20px;
          ${playIconCustomCss && playIconCustomCss}
        }
        .jw-display-icon-container.jw-display-icon-display.jw-reset > div .jw-svg-icon-play path {
            display: none;
          }

      `);
  }

  const appendJWStyle = (optionName: string, value: string) => {
    if (!['play', 'pause'].includes(optionName)) {
      $('style').append(`
            .jw-icon-${optionName} svg{
            background: url("${value}");
            background-size: contain;
            background-repeat: no-repeat;
            background-position: left center;
          }

          .jw-icon-${optionName} path {
            display: none !important;
          }
        `);
    } else {
      $('style').append(`
      .jw-icon.jw-icon-inline.jw-reset .jw-svg-icon-${optionName} {
          background: url("${value}");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: left center;
        }

         .jw-icon.jw-icon-inline.jw-reset .jw-svg-icon-${optionName} path {
          display: none !important;
        }
      `);
    }
  };

  if (customIcon) {
    Object.entries(customIcon).forEach(([key, value]) => {
      appendJWStyle(key, value);
    });
  }
  _playerOptions.skin.name = 'customskin';
};

const setVolume = (playerInstance: JWPlayer, volume: number | undefined) => {
  // 재생 시작 시 볼륨 크기 설정 : volume (0~100)
  if (_playerOptions.mute !== true) {
    playerInstance.setVolume(volume || 100);
  }
};

const setFullscreenOnPlay = (
  playFullscreenOption: boolean | undefined,
  playerInstance: JWPlayer,
) => {
  if (playFullscreenOption !== true) return;
  let autostartChecked = _playerOptions.autostart;

  playerInstance.on('play', function () {
    if (autostartChecked) {
      // autostart 시 true→false 를 연달아 호출하는 의도된 우회 코드.
      // 상태가 남지 않으므로 실패 감시 대상이 아니다.
      playerInstance.setFullscreen(true);
      playerInstance.setFullscreen(false);
      autostartChecked = !autostartChecked;
      return;
    }
    requestFullscreenWithReport(playerInstance, 'playOption');
  });
};

/**
 * Sentry 계측 배선.
 *
 * - 채널/오브젝트 식별자를 태그로 붙인다. 없으면 이슈만 쌓이고 어느 채널인지 알 수 없다.
 * - autostartNotAllowed: JW 가 자동재생 차단을 알려주는 전용 이벤트.
 *   window.onerror 로는 올라오지 않고, base.pug 의 ignoreErrors 가
 *   'NotAllowedError' 를 필터링하므로 이 이벤트가 유일한 관측 경로다.
 */
const setupPlayerFailureReport = (playerInstance: JWPlayer) => {
  setSentryTags({
    channelId: _playerOptions.x_midibus_ch_id,
    objectId: _playerOptions.x_midibus_ob_id,
  });

  let autostartReported = false;
  playerInstance.on('autostartNotAllowed', (evt) => {
    if (autostartReported) return;
    autostartReported = true;

    captureFailure('JW autostart not allowed', {
      tags: { feature: 'autostart' },
      extra: {
        code: evt.code,
        reason: evt.reason,
        message: evt.error && evt.error.message,
        mute: _playerOptions.mute,
        userAgent: navigator.userAgent,
      },
    });
  });
};

const isPlaylist = () => window.location.pathname.includes('playlist');

const setNoRewind = (noRewindOption: boolean, playerInstance: JWPlayer) => {
  if (noRewindOption !== false) return;

  const setCompleteErrorMessage = () => {
    playerInstance.load([]);
    playerInstance.on('error', () => {
      const jwErrorElement = document.getElementsByClassName(
        'jw-error-text jw-reset-text',
      )[0];
      jwErrorElement.innerHTML = `<p class="err-message">${FRONTEND_MESSAGES.playbackCompleted}</p>`;
    });
  };

  if (isPlaylist()) {
    playerInstance.on('complete', () => {
      if (
        playerInstance.getPlaylistIndex() ===
        playerInstance.getPlaylist().length - 1
      )
        setCompleteErrorMessage();
    });
  } else {
    playerInstance.on('complete', () => {
      setCompleteErrorMessage();
    });
  }
};

const setNoSkip = (noSkipOption: boolean) => {
  if (noSkipOption !== true) return;
  // CSS (display: none) is handled by server-side Pug template

  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.stopImmediatePropagation();
      }
    },
    true,
  );
};

const setPlayerUi = (uiOption) => {
  if (uiOption.speed === true && uiOption.quality === false) {
    jwplayer().on('ready', () => {
      $('.jw-icon-settings').on('click', () => {
        $('.jw-settings-quality').css('display', 'none');
        $('#jw-player-settings-submenu-quality').css('display', 'none');

        if (document.querySelector('.jw-settings-captions')) {
          if (
            document.querySelector('.jw-settings-submenu-captions')
              .ariaExpanded === 'false'
          ) {
            $('.jw-settings-playbackRates').attr('aria-expanded', 'true');
            $('#jw-player-settings-submenu-playbackRates')
              .attr('aria-expanded', 'true')
              .css('display', 'block');
          }
        }
      });

      $('[aria-controls="jw-player-settings-submenu-captions"]').on(
        'click',
        () => {
          $('.jw-settings-playbackRates').attr('aria-expanded', 'false');
          document.querySelector('.jw-settings-playbackRates');

          $('#jw-player-settings-submenu-playbackRates')
            .attr('aria-expanded', 'false')
            .css('display', 'none');
        },
      );
    });
  }
  // quality === false && speed === false: CSS handled by server-side Pug template
};

export default async () => {
  if (window.self !== window.top) {
    document.body.classList.add('in-iframe');
  }

  const playerInstance = jwplayer();
  const { customOption, skin } = _playerOptions;
  const { uiOption } = customOption;

  try {
    await playerCustomOptionSchema.validateAsync(customOption);
  } catch (e) {
    console.error('Validation error: ' + e.message);
  }

  if (skin) {
    try {
      await playerSkinOptionSchema.validateAsync(skin);
    } catch (e) {
      console.error('Validation error: ' + e.message);
    }
  }

  setPlayerUi(uiOption);
  setSeekStep(playerInstance, customOption.seekStep);
  setCustomIcon();
  addMobileTapSkipFeature();

  if (_playerOptions.customOption?.watermark?.enable) {
    printWatermarkJW(
      _playerOptions.customOption?.watermark.label,
      _playerOptions.customOption?.watermark,
    );
  }

  playerInstance.on('levels', () => {
    setVolume(playerInstance, customOption.volume);
  });

  setupPlayerFailureReport(playerInstance);

  playerInstance.on('ready', () => {
    disableResizeMobilePlayer(customOption.mobileSizing);
    setFullscreenOnPlay(customOption.playFullscreen, playerInstance);
    setNoRewind(customOption.rewind, playerInstance);
    setNoSkip(customOption.noSkip);
    if (customOption.addSkipBtn) {
      addSkipBtn();
    }

    if (customOption.clickToPlayPause && !jwplayer().getControls()) {
      const overlayEl = document.querySelector('.mbus-overlay') as HTMLElement;
      overlayEl.classList.add('clickable', 'active');
      overlayEl.addEventListener('click', () => {
        playerInstance.playToggle();
      });
    }
  });

  if (!isMobile()) {
    playerInstance.on('playbackRateChanged', ({ playbackRate }) => {
      savePlaybackRate(playbackRate);
    });
  }
};
